import { Container, IDestroyOptions } from "@pixi/display"
import { GameModel } from "../../../models/game/GameModel";
import { FigureViewFactory } from "../figures/FigureViewFactory";
import { DeckFigureView } from "../figures/DeckFigureView";
import { GameViewController } from "../../../controllers/GameViewController";
import { Event } from "event-system";
import { FederatedPointerEvent } from "@pixi/events";
import { BaseFigureView } from "../figures/BaseFigureView";
import { ShuffleBoosterButton } from "./boosters/ShuffleBoosterButton";
import { AudioManager } from "../../../../../audio/AudioManager";
import { PlayerModel } from "../../../models/player/PlayerModel";
import { EraseTrianglesBoosterButton } from "./boosters/EraseTrianglesBoosterButton";
import { EraseBoosterButton } from "./boosters/EraseBoosterButton";
import { GameUIResourcesPackage } from "../../../../preloader/resources/GameUIResourcesPackage";

export class DeckView extends Container {
    public onStartMoveFigure: Event<BaseFigureView, FederatedPointerEvent>;
    private figures: Array<DeckFigureView>;
    private movedFigure: DeckFigureView;

    private readonly shuffleBooster: ShuffleBoosterButton;
    private readonly eraseTrianglesBooster: EraseTrianglesBoosterButton;
    private readonly eraseBooster: EraseBoosterButton;

    private blockedFigures: Set<number>;

    public constructor(
        private readonly gameUIResourcesPackage: GameUIResourcesPackage,
        private readonly figureViewFactory: FigureViewFactory,
        private readonly gameModel: GameModel,
        private readonly playerModel: PlayerModel,
        private readonly gameViewController: GameViewController,
        audioManager: AudioManager
    ) {
        super();
        this.onStartMoveFigure = new Event<DeckFigureView, FederatedPointerEvent>;
        this.figures = new Array<DeckFigureView>(this.gameModel.deckModel.deck.length);
        this.blockedFigures = new Set<number>();
        this.shuffleBooster = new ShuffleBoosterButton(this.playerModel, this.gameModel, this.gameViewController, audioManager, this.gameUIResourcesPackage);
        this.eraseTrianglesBooster = new EraseTrianglesBoosterButton(this.playerModel, this.gameModel, this.gameViewController, audioManager, this.gameUIResourcesPackage);
        this.eraseBooster = new EraseBoosterButton(this.playerModel, this.gameModel, this.gameViewController, audioManager, this.gameUIResourcesPackage);
        this.addChild(this.shuffleBooster, this.eraseTrianglesBooster, this.eraseBooster);
        this.shuffleBooster.position.set(0, 25);
        this.eraseTrianglesBooster.position.set(386.5, 25);
        this.eraseBooster.position.set(770, 25);
        this.createFigures();
        this.gameModel.onReject.add(this.revertMovedFigure, this);
        this.gameModel.onChanged.add(this.destroyMovedFigure, this);
        this.gameModel.deckModel.onChangedSpecialSlot.add(this.handleSpecialSlotFigure, this);
        this.gameModel.deckModel.onChanged.add(this.createFigures, this);
        this.gameModel.onCanUseFigure.add(this.handleBlockFigure, this);
    }

    private handleSpecialSlotFigure(): void {
        if (!this.gameViewController.isSpecialSlotActive()) {
            const figure = this.figures[this.gameModel.deckModel.deck.length - 1];
            figure?.onSelected.remove(this.selectFigure, this);
            this.removeChild(figure)?.destroy();
            this.eraseBooster.visible = true;
        } else {
            const figure = this.addChild(this.figureViewFactory.createDeckFigureView(this.gameModel.deckModel.deck[this.gameModel.deckModel.deck.length - 1]));
            figure.onSelected.add(this.selectFigure, this);
            figure.position.set(
                this.eraseBooster.position.x + (this.eraseBooster.width - figure.width) * 0.5,
                this.eraseBooster.position.y + (this.eraseBooster.height - figure.height) * 0.5
            );
            this.figures[this.gameModel.deckModel.deck.length - 1] = figure;
            this.eraseBooster.visible = false;
            this.shuffleBooster.shuffleButton.stopAnimation();
        }
    }

    private handleBlockFigure(figureIndex: number, canPlaceThisFigure: boolean): void {
        if (canPlaceThisFigure) {
            this.blockedFigures.delete(figureIndex);
            this.updateFigureState(figureIndex);
        } else {
            this.blockedFigures.add(figureIndex);
            this.updateFigureState(figureIndex);
        }
    }

    private updateFigureState(figureIndex: number): void {
        if (figureIndex >= 0 && figureIndex < this.figures.length) {
            const figureView = this.figures[figureIndex];
            const isBlocked = this.blockedFigures.has(figureIndex);
            figureView.alpha = isBlocked ? 0.5 : 1;
            figureView.eventMode = isBlocked ? "none" : "static";
        }
    }

    private async createFigures(): Promise<void> {
        for (let i = 0; i < this.gameModel.deckModel.deck.length - 1; i++) {
            const f = this.figures[i];
            if (f == null) {
                continue;
            }
            f.onSelected.removeAll();
            this.removeChild(f).destroy();
        }
        const positions = [
            227,
            467,
            714
        ];
        for (let i = 0; i < this.gameModel.deckModel.deck.length - 1; i++) {
            if (this.gameModel.deckModel.deck[i] == null) {
                continue;
            }

            const figure = this.addChild(this.figureViewFactory.createDeckFigureView(this.gameModel.deckModel.deck[i]));
            figure.position.set(positions[i] - figure.width * 0.5, (510 - figure.height) * 0.5);
            this.figures[i] = figure;
            figure.onSelected.add(this.selectFigure, this);
            figure.playShowAnimation();
            this.updateFigureState(i);
        }
    }

    private async selectFigure(figure: DeckFigureView, event: FederatedPointerEvent): Promise<void> {
        const index = this.figures.indexOf(figure);
        if (this.blockedFigures.has(index)) {
            return;
        }
        this.gameViewController.selectFigure(index);
        this.movedFigure = this.figureViewFactory.createDeckFigureView(this.gameModel.deckModel.deck[index]);
        this.addChild(this.movedFigure);
        this.movedFigure.position = figure.position;
        figure.visible = false;
        const endPosition = event.getLocalPosition(this);
        endPosition.y -= this.movedFigure.height + 100;
        endPosition.x -= this.movedFigure.width * 0.5;
        this.movedFigure.playStartingAnimation(endPosition);
        this.onStartMoveFigure.emit(this.movedFigure, event);
    }

    private destroyMovedFigure(): void {
        this.removeChild(this.movedFigure)?.destroy();
        this.movedFigure = null;
    }

    public async revertMovedFigure(): Promise<void> {
        await this.movedFigure.playRevertAnimation(this.figures[this.gameModel.deckModel.SelectedFigureIndex].position);
        this.destroyMovedFigure();
        this.figures[this.gameModel.deckModel.SelectedFigureIndex].visible = true;
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.gameModel.deckModel.onChanged.remove(this.createFigures, this);
        this.gameModel.onChanged.remove(this.destroyMovedFigure, this);
        this.gameModel.deckModel.onChangedSpecialSlot.remove(this.handleSpecialSlotFigure, this);
        this.gameModel.onReject.remove(this.revertMovedFigure, this);
        this.gameModel.onCanUseFigure.remove(this.handleBlockFigure, this);
        this.figures.forEach(f => f.onSelected.removeAll());
        super.destroy(options);
    }
}